import React from "react";
import toast from "react-hot-toast";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  Dumpster,
  DumpsterSize,
  dumpsterSizeToText,
  dumpsterLocationTypeToText,
  dumpsterLocationBadgeText,
  dumpsterFillStatusToText,
} from "../../../../models";
import {
  getDumpsters,
  toggleShowAddDumpsterModal,
  toggleShowEditDumpsterModal,
  showDeleteDumpsterConfirmationModal,
  setSelectedDumpster,
  setShowSetLocationModal,
} from "../../dumpstersSlice";
import { getDumpYardTemplates } from "../../../DumpYards/dumpYardsSlice";
import { getVehicles } from "../../../Vehicles/vehiclesSlice";

import "./DumpstersView.scss";

export function DumpstersView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const dumpsters = useAppSelector((state) => state.dumpsters.dumpsters) || [];
  const getDumpstersAPI = useAppSelector(
    (state) => state.dumpsters.getDumpsters
  );
  const templates = useAppSelector((state) => state.dumpYards.templates) || [];
  const storageYards = React.useMemo(
    () => templates.filter((t) => t.isStorage),
    [templates]
  );

  const vehicles = useAppSelector((state) => state.vehicles.vehicles) || [];
  const vehicleLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    vehicles.forEach((v) => {
      map[v.id] = v.label;
    });
    return map;
  }, [vehicles]);

  const [search, setSearch] = React.useState<string>("");
  const [sizeFilter, setSizeFilter] = React.useState<string>("");
  const [locationFilter, setLocationFilter] = React.useState<string>("");
  const [fillFilter, setFillFilter] = React.useState<string>("");
  const [summaryExpanded, setSummaryExpanded] = React.useState<boolean>(true);

  React.useEffect(() => {
    dispatch(getDumpsters({}));
    dispatch(getDumpYardTemplates());
    dispatch(getVehicles({}));
  }, [dispatch]);

  React.useEffect(() => {
    if (!getDumpstersAPI.loading && getDumpstersAPI.error) {
      toast.error("Failed to load dumpsters. Please try again.");
    }
  }, [getDumpstersAPI.loading, getDumpstersAPI.error]);

  const filteredDumpsters = React.useMemo(() => {
    return dumpsters.filter((d) => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          d.label.toLowerCase().includes(searchLower) ||
          d.currentAddress?.toLowerCase().includes(searchLower) ||
          d.currentCity?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Size filter
      if (sizeFilter && d.dumpsterSize !== sizeFilter) {
        return false;
      }

      // Location type filter
      if (locationFilter && d.locationType !== locationFilter) {
        return false;
      }

      // Fill status filter
      if (fillFilter && d.fillStatus !== fillFilter) {
        return false;
      }

      return true;
    });
  }, [dumpsters, search, sizeFilter, locationFilter, fillFilter]);

  const handleAddDumpster = () => {
    dispatch(toggleShowAddDumpsterModal());
  };

  const handleEditDumpster = (dumpster: Dumpster) => {
    dispatch(setSelectedDumpster(dumpster));
    dispatch(toggleShowEditDumpsterModal());
  };

  const handleDeleteDumpster = (dumpster: Dumpster) => {
    dispatch(showDeleteDumpsterConfirmationModal(dumpster));
  };

  const handleSetLocation = (dumpster: Dumpster) => {
    dispatch(setShowSetLocationModal(dumpster));
  };

  const handleRowClick = (dumpster: Dumpster) => {
    navigate(`/dumpsters/${dumpster.id}`);
  };

  const handleClearFilters = () => {
    setSearch("");
    setSizeFilter("");
    setLocationFilter("");
    setFillFilter("");
  };

  const hasActiveFilters = search || sizeFilter || locationFilter || fillFilter;

  // Compute summary data
  const summaryData = React.useMemo(() => {
    const countBySize = (list: Dumpster[]) => ({
      size10: list.filter((d) => d.dumpsterSize === DumpsterSize.SIZE_10).length,
      size15: list.filter((d) => d.dumpsterSize === DumpsterSize.SIZE_15).length,
      cleanFill: list.filter((d) => d.dumpsterSize === DumpsterSize.SIZE_CLEAN_FILL).length,
      total: list.length,
    });

    // Group storage dumpsters by storage yard
    const storageRows: { name: string; counts: ReturnType<typeof countBySize> }[] = [];

    storageYards.forEach((yard) => {
      const yardDumpsters = dumpsters.filter(
        (d) => d.locationType === "storage" && d.currentStorageYardId === yard.id
      );
      if (yardDumpsters.length > 0) {
        storageRows.push({
          name: yard.name,
          counts: countBySize(yardDumpsters),
        });
      }
    });

    // Dumpsters in storage without a valid storage yard ID
    const unmappedStorage = dumpsters.filter(
      (d) => d.locationType === "storage" && !storageYards.find((y) => y.id === d.currentStorageYardId)
    );
    if (unmappedStorage.length > 0) {
      storageRows.push({
        name: "Other Storage",
        counts: countBySize(unmappedStorage),
      });
    }

    // Order dumpsters
    const orderDumpsters = dumpsters.filter((d) => d.locationType === "order");
    const orderRow = {
      name: "Order",
      counts: countBySize(orderDumpsters),
    };

    // On Truck dumpsters
    const onTruckDumpsters = dumpsters.filter((d) => d.locationType === "on_truck");
    const onTruckRow = {
      name: "On Truck",
      counts: countBySize(onTruckDumpsters),
    };

    // Unknown dumpsters
    const unknownDumpsters = dumpsters.filter((d) => d.locationType === "unknown");
    const unknownRow = {
      name: "Unknown",
      counts: countBySize(unknownDumpsters),
    };

    // Total
    const totalRow = {
      name: "Total",
      counts: countBySize(dumpsters),
    };

    return {
      storageRows,
      orderRow,
      onTruckRow,
      unknownRow,
      totalRow,
    };
  }, [dumpsters, storageYards]);

  if (getDumpstersAPI.loading && dumpsters.length === 0) {
    return (
      <div className="dumpsters-view loading">
        <div className="loading-text">Loading dumpsters...</div>
      </div>
    );
  }

  return (
    <div className="dumpsters-view">
      <div className="header-filters-section">
        <div className="header-row">
          <div className="title-section">
            <h2>
              Dumpsters
              <span className="count">({dumpsters.length})</span>
            </h2>
            <p className="subtitle">
              Manage your dumpster inventory and track locations
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/dumpsters/map")}
            >
              View Map
            </button>
            <button className="btn btn-primary" onClick={handleAddDumpster}>
              + Add Dumpster
            </button>
          </div>
        </div>

        <div className="filters-row">
          <div className="filter-group search-group">
            <label>Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by label or location..."
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Size</label>
            <select
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Sizes</option>
              <option value={DumpsterSize.SIZE_10}>
                {dumpsterSizeToText[DumpsterSize.SIZE_10]}
              </option>
              <option value={DumpsterSize.SIZE_15}>
                {dumpsterSizeToText[DumpsterSize.SIZE_15]}
              </option>
              <option value={DumpsterSize.SIZE_CLEAN_FILL}>
                {dumpsterSizeToText[DumpsterSize.SIZE_CLEAN_FILL]}
              </option>
            </select>
          </div>
          <div className="filter-group">
            <label>Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Locations</option>
              <option value="unknown">
                {dumpsterLocationTypeToText["unknown"]}
              </option>
              <option value="storage">
                {dumpsterLocationTypeToText["storage"]}
              </option>
              <option value="order">
                {dumpsterLocationTypeToText["order"]}
              </option>
              <option value="on_truck">
                {dumpsterLocationTypeToText["on_truck"]}
              </option>
            </select>
          </div>
          <div className="filter-group">
            <label>Fill</label>
            <select
              value={fillFilter}
              onChange={(e) => setFillFilter(e.target.value)}
              className="filter-input"
            >
              <option value="">All Fill Levels</option>
              <option value="empty">{dumpsterFillStatusToText["empty"]}</option>
              <option value="half">{dumpsterFillStatusToText["half"]}</option>
              <option value="full">{dumpsterFillStatusToText["full"]}</option>
            </select>
          </div>
          <div className="filter-actions">
            {hasActiveFilters && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="summary-card">
        <div
          className="summary-header"
          onClick={() => setSummaryExpanded(!summaryExpanded)}
        >
          <h3>Inventory Summary</h3>
          {summaryExpanded ? (
            <BsChevronUp className="collapse-icon" />
          ) : (
            <BsChevronDown className="collapse-icon" />
          )}
        </div>
        {summaryExpanded && (
          <div className="summary-content">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>10 Yards</th>
                  <th>15 Yards</th>
                  <th>Clean Fill</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {summaryData.storageRows.map((row) => (
                  <tr key={row.name}>
                    <td className="location-name">{row.name}</td>
                    <td>{row.counts.size10 || "-"}</td>
                    <td>{row.counts.size15 || "-"}</td>
                    <td>{row.counts.cleanFill || "-"}</td>
                    <td className="total-cell">{row.counts.total}</td>
                  </tr>
                ))}
                {summaryData.orderRow.counts.total > 0 && (
                  <tr>
                    <td className="location-name">{summaryData.orderRow.name}</td>
                    <td>{summaryData.orderRow.counts.size10 || "-"}</td>
                    <td>{summaryData.orderRow.counts.size15 || "-"}</td>
                    <td>{summaryData.orderRow.counts.cleanFill || "-"}</td>
                    <td className="total-cell">{summaryData.orderRow.counts.total}</td>
                  </tr>
                )}
                {summaryData.onTruckRow.counts.total > 0 && (
                  <tr>
                    <td className="location-name">{summaryData.onTruckRow.name}</td>
                    <td>{summaryData.onTruckRow.counts.size10 || "-"}</td>
                    <td>{summaryData.onTruckRow.counts.size15 || "-"}</td>
                    <td>{summaryData.onTruckRow.counts.cleanFill || "-"}</td>
                    <td className="total-cell">{summaryData.onTruckRow.counts.total}</td>
                  </tr>
                )}
                {summaryData.unknownRow.counts.total > 0 && (
                  <tr>
                    <td className="location-name">{summaryData.unknownRow.name}</td>
                    <td>{summaryData.unknownRow.counts.size10 || "-"}</td>
                    <td>{summaryData.unknownRow.counts.size15 || "-"}</td>
                    <td>{summaryData.unknownRow.counts.cleanFill || "-"}</td>
                    <td className="total-cell">{summaryData.unknownRow.counts.total}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td className="location-name">{summaryData.totalRow.name}</td>
                  <td>{summaryData.totalRow.counts.size10 || "-"}</td>
                  <td>{summaryData.totalRow.counts.size15 || "-"}</td>
                  <td>{summaryData.totalRow.counts.cleanFill || "-"}</td>
                  <td className="total-cell">{summaryData.totalRow.counts.total}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {filteredDumpsters.length === 0 ? (
        <div className="empty-state">
          <div className="empty-message">
            <h3>No dumpsters found</h3>
            <p>
              {hasActiveFilters
                ? "No dumpsters match your filter criteria. Try adjusting your filters."
                : "Get started by adding your first dumpster to the inventory."}
            </p>
            {!hasActiveFilters && (
              <button className="btn btn-primary" onClick={handleAddDumpster}>
                + Add Dumpster
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="dumpsters-table-container">
          <div className="table-scroll-wrapper">
            <table className="dumpsters-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Size</th>
                  <th>Fill</th>
                  <th>Location</th>
                  <th>Address</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDumpsters.map((dumpster: Dumpster) => (
                  <tr
                    key={dumpster.id}
                    className="clickable-row"
                    onClick={() => handleRowClick(dumpster)}
                  >
                    <td className="label-cell">
                      <div className="label">{dumpster.label}</div>
                    </td>
                    <td className="size-cell">
                      <div className="size">
                        {dumpsterSizeToText[dumpster.dumpsterSize]}
                      </div>
                    </td>
                    <td className="fill-cell">
                      <span className={`fill-badge fill-${dumpster.fillStatus}`}>
                        {dumpsterFillStatusToText[dumpster.fillStatus]}
                      </span>
                    </td>
                    <td className="location-cell">
                      <span
                        className={`location-badge location-${dumpster.locationType}`}
                      >
                        {dumpsterLocationBadgeText(
                          dumpster.locationType,
                          dumpster.currentVehicleId
                            ? vehicleLabelById[dumpster.currentVehicleId]
                            : undefined
                        )}
                      </span>
                    </td>
                    <td className="address-cell">
                      {dumpster.currentAddress ||
                      dumpster.currentCity ||
                      dumpster.currentZip ? (
                        <div className="address">
                          {dumpster.currentAddress && (
                            <div className="street">{dumpster.currentAddress}</div>
                          )}
                          <div className="city-zip">
                            {[dumpster.currentCity, dumpster.currentZip]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </div>
                      ) : (
                        <span className="no-address">-</span>
                      )}
                    </td>
                    <td className="notes-cell">
                      <div className="notes">{dumpster.notes || "-"}</div>
                    </td>
                    <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-location"
                          onClick={() => handleSetLocation(dumpster)}
                        >
                          Set Location
                        </button>
                        <button
                          className="btn btn-sm btn-edit"
                          onClick={() => handleEditDumpster(dumpster)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteDumpster(dumpster)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
